import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SendIcon from '@mui/icons-material/Send';
import {
  formatContextChipLabel,
} from '../utils/boardSelectionContext';
import BoardAiThinking from './BoardAiThinking';
import BoardAiJobDebug from './BoardAiJobDebug';
import BoardAiInputDebug from './BoardAiInputDebug';
import BoardChatArtifact from './BoardChatArtifact';

function resizeChatInput(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
}

function formatMessageTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Split reply text into paragraphs so each can get a first-line indent. */
function splitChatParagraphs(content) {
  const text = String(content ?? '');
  if (!text) return [];
  const parts = text
    .split(/\n\s*\n/)
    .map(part => part.replace(/^\n+|\n+$/g, ''))
    .filter(part => part.length > 0);
  return parts.length > 0 ? parts : [text];
}

function ChatAnswerParagraphs({ content, className = 'board-chat-message-paragraph' }) {
  const paragraphs = splitChatParagraphs(content);
  if (paragraphs.length === 0) return null;
  return paragraphs.map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

function getSourceHostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}

function SourceCard({ source, onPinSource }) {
  const hostname = getSourceHostname(source.url);
  const [imageFailed, setImageFailed] = useState(false);
  const [pinned, setPinned] = useState(false);
  const showImage = Boolean(source.imageUrl) && !imageFailed;

  const handlePin = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!onPinSource || pinned) return;
    const ok = onPinSource(source);
    if (ok !== false) setPinned(true);
  };

  return (
    <Box className="board-chat-source-card">
      <a
        className="board-chat-source-link"
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        title={source.title || source.url}
      >
        <Box className="board-chat-source-image-wrap">
          {showImage ? (
            <img
              src={source.imageUrl}
              alt=""
              className="board-chat-source-image"
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setImageFailed(true)}
            />
          ) : null}
          <Box
            className="board-chat-source-image-fallback"
            style={{ display: showImage ? 'none' : 'flex' }}
          >
            <LinkOutlinedIcon fontSize="small" />
          </Box>
        </Box>
        <Box className="board-chat-source-body">
          <Typography variant="caption" className="board-chat-source-title">
            {source.title || hostname || source.url}
          </Typography>
          {hostname ? (
            <Typography variant="caption" color="text.secondary" className="board-chat-source-host">
              {hostname}
            </Typography>
          ) : null}
        </Box>
      </a>
      {onPinSource ? (
        <Button
          size="small"
          className={`board-chat-source-pin-btn${pinned ? ' is-pinned' : ''}`}
          startIcon={pinned ? <CheckIcon sx={{ fontSize: 14 }} /> : <PushPinIcon sx={{ fontSize: 14 }} />}
          onClick={handlePin}
          disabled={pinned}
        >
          {pinned ? 'Pinned' : 'Pin'}
        </Button>
      ) : null}
    </Box>
  );
}

function ContextChipIcon({ kind }) {
  if (kind === 'user-pin') return <PushPinOutlinedIcon sx={{ fontSize: 14 }} />;
  if (kind === 'ai-pin') return <SmartToyOutlinedIcon sx={{ fontSize: 14 }} />;
  return <CropSquareOutlinedIcon sx={{ fontSize: 14 }} />;
}

function contextChipClassName(kind) {
  if (kind === 'user-pin') return 'board-chat-context-chip board-chat-context-chip--user-pin';
  if (kind === 'ai-pin') return 'board-chat-context-chip board-chat-context-chip--ai-pin';
  return 'board-chat-context-chip';
}

const CONVERSATION_GROUPS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Previous 7 days' },
  { id: 'earlier', label: 'Earlier' },
];

function conversationBucket(value) {
  const time = new Date(value || 0).getTime();
  if (!Number.isFinite(time) || time <= 0) return 'earlier';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (time >= startOfToday) return 'today';
  if (time >= startOfToday - 86400000) return 'yesterday';
  if (time >= startOfToday - 6 * 86400000) return 'week';
  return 'earlier';
}

function groupConversations(conversations) {
  const buckets = {
    today: [],
    yesterday: [],
    week: [],
    earlier: [],
  };
  for (const conversation of conversations) {
    const bucket = conversationBucket(conversation.updatedAt || conversation.createdAt);
    buckets[bucket].push(conversation);
  }
  return CONVERSATION_GROUPS
    .map(group => ({ ...group, items: buckets[group.id] }))
    .filter(group => group.items.length > 0);
}

function ConversationSidebar({
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  bootstrapping,
  creatingConversation,
  loading,
}) {
  const groups = groupConversations(conversations);

  return (
    <Box className="board-chat-sidebar" aria-label="Previous chats">
      <Button
        variant="outlined"
        className="board-chat-sidebar-new"
        startIcon={creatingConversation ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
        onClick={onNewConversation}
        disabled={loading || bootstrapping || creatingConversation}
      >
        New chat
      </Button>
      <Box className="board-chat-sidebar-list">
        {bootstrapping && conversations.length === 0 ? (
          <Box className="board-chat-sidebar-status">
            <CircularProgress size={14} />
            <span>Loading chats…</span>
          </Box>
        ) : null}
        {!bootstrapping && conversations.length === 0 ? (
          <Typography variant="caption" className="board-chat-sidebar-status">
            No previous chats
          </Typography>
        ) : null}
        {groups.map(group => (
          <Box key={group.id} className="board-chat-sidebar-group">
            <Typography variant="caption" component="div" className="board-chat-sidebar-group-label">
              {group.label}
            </Typography>
            {group.items.map(conversation => {
              const active = conversation.id === activeConversationId;
              const title = conversation.title || 'Untitled chat';
              return (
                <Box
                  key={conversation.id}
                  className={`board-chat-sidebar-item${active ? ' is-active' : ''}`}
                  role="button"
                  tabIndex={0}
                  title={title}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => onSelectConversation(conversation.id)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectConversation(conversation.id);
                    }
                  }}
                >
                  <span className="board-chat-sidebar-item-title">{title}</span>
                  <IconButton
                    size="small"
                    className="board-chat-sidebar-delete"
                    aria-label={`Delete ${title}`}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onDeleteConversation(conversation.id);
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const BoardChatPanel = ({
  open,
  onClose,
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  messages,
  input,
  onInputChange,
  onSend,
  loading,
  bootstrapping,
  creatingConversation = false,
  error,
  thinkingPhase = '',
  lastJobMetrics = null,
  composerContextItems,
  onRemoveComposerContextItem,
  pinLabelById,
  onPinConversation,
  onPinExchange,
  onPinSource,
  onPlaceArtifact,
  inAppAskLabel = 'In app',
  chatModel = 'better',
  chatModelOptions = [],
  onChatModelChange,
  chatModelMenuLabel = 'Choose AI model',
  focusMessageAt,
  onFocusMessageHandled,
}) => {
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const focusMessageRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [conversationMenuOpen, setConversationMenuOpen] = useState(false);
  const [modelMenuAnchor, setModelMenuAnchor] = useState(null);

  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, messages, loading]);

  useEffect(() => {
    if (!open || !focusMessageAt) return undefined;
    const timer = window.setTimeout(() => {
      if (focusMessageRef.current) {
        focusMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onFocusMessageHandled?.();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [open, focusMessageAt, messages, onFocusMessageHandled]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open, activeConversationId]);

  useLayoutEffect(() => {
    resizeChatInput(inputRef.current);
  }, [open, expanded, input]);

  useEffect(() => {
    if (!open) {
      setConversationMenuOpen(false);
      setModelMenuAnchor(null);
    }
  }, [open]);

  if (!open) return null;

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  const activeConversation = conversations.find(item => item.id === activeConversationId) || null;
  const headerTitle = activeConversation?.title || 'New chat';
  const showEmptyState = messages.length === 0 && !loading;
  const activeChatModel = chatModelOptions.find(option => option.id === chatModel)
    || chatModelOptions[0]
    || null;

  return (
    <>
    <Box
      className="board-chat-backdrop"
      onClick={onClose}
      aria-hidden="true"
    />
    <Box
      className={`board-chat-panel${expanded ? ' board-chat-panel--expanded' : ''}`}
      role="dialog"
      aria-label="Board chat"
    >
      {expanded ? (
        <ConversationSidebar
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={onSelectConversation}
          onNewConversation={onNewConversation}
          onDeleteConversation={onDeleteConversation}
          bootstrapping={bootstrapping}
          creatingConversation={creatingConversation}
          loading={loading}
        />
      ) : null}
      <Box className="board-chat-main">
      <Box className={`board-chat-header${expanded ? '' : ' board-chat-header--compact'}`}>
        {expanded ? (
          <Box className="board-chat-header-main">
            <Typography variant="subtitle2" className="board-chat-title">
              {headerTitle}
            </Typography>
          </Box>
        ) : (
          <Select
            size="small"
            value={activeConversationId || ''}
            open={conversationMenuOpen}
            onOpen={() => setConversationMenuOpen(true)}
            onClose={() => setConversationMenuOpen(false)}
            onChange={event => {
              const next = event.target.value;
              if (next === '__loading__') return;
              onSelectConversation(next);
            }}
            className="board-chat-conversation-select"
            displayEmpty
            renderValue={selected => {
              const active = conversations.find(item => item.id === selected);
              const label = active?.title || 'New chat';
              return (
                <Box className="board-chat-conversation-select-value">
                  <span className="board-chat-conversation-select-label">{label}</span>
                </Box>
              );
            }}
            MenuProps={{
              style: { zIndex: 1500 },
            }}
          >
            {bootstrapping && conversationMenuOpen ? (
              <MenuItem disabled value="__loading__" className="board-chat-conversation-loading-item">
                <CircularProgress size={14} className="board-chat-conversation-spinner" />
                Loading chats…
              </MenuItem>
            ) : null}
            {conversations.length === 0 ? (
              <MenuItem value="">New chat</MenuItem>
            ) : (
              conversations.map(conversation => (
                <MenuItem key={conversation.id} value={conversation.id}>
                  {conversation.title || 'Untitled chat'}
                </MenuItem>
              ))
            )}
          </Select>
        )}
        <Box className="board-chat-header-actions">
          {!expanded ? (
            <>
              <Tooltip title="New conversation">
                <span>
                  <IconButton
                    size="small"
                    onClick={onNewConversation}
                    aria-label="New conversation"
                    disabled={loading || bootstrapping || creatingConversation}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {activeConversationId ? (
                <Tooltip title="Delete conversation">
                  <IconButton
                    size="small"
                    onClick={() => onDeleteConversation(activeConversationId)}
                    aria-label="Delete conversation"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          ) : null}
          {activeConversationId && messages.length > 0 ? (
            <Tooltip title="Pin this chat on the canvas">
              <IconButton
                size="small"
                onClick={onPinConversation}
                aria-label="Pin chat on canvas"
                className="board-chat-pin-conversation-btn"
              >
                <PushPinOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={expanded ? 'Shrink chat' : 'Expand chat'}>
            <IconButton
              size="small"
              onClick={() => setExpanded(prev => !prev)}
              aria-label={expanded ? 'Shrink chat' : 'Expand chat'}
              aria-pressed={expanded}
            >
              {expanded ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} aria-label="Close chat">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box className="board-chat-messages">
        {showEmptyState ? (
          <Box className="board-chat-empty">
            <Typography className="board-chat-empty-title">
              Ask AI anything for your goal.
            </Typography>
            <Typography variant="body2" color="text.secondary" className="board-chat-empty-subtitle">
              If you like the answer, pin it to the board and connect it to your visual memories.
            </Typography>
          </Box>
        ) : null}
        {messages.map((message, index) => {
          const isFocused = Boolean(
            focusMessageAt
            && message.role === 'assistant'
            && (
              String(message.at || '') === String(focusMessageAt)
              || String(index) === String(focusMessageAt)
            )
          );
          const canPinExchange = message.role === 'assistant'
            && messages[index - 1]?.role === 'user';

          return (
          <Box
            key={`${message.at || index}-${message.role}`}
            ref={isFocused ? focusMessageRef : undefined}
            className={`board-chat-message board-chat-message--${message.role}${isFocused ? ' board-chat-message--focused' : ''}`}
          >
            {(formatMessageTime(message.at) || canPinExchange) ? (
            <Box className="board-chat-message-header">
              {formatMessageTime(message.at) ? (
                <Typography variant="caption" className="board-chat-message-meta">
                  {formatMessageTime(message.at)}
                </Typography>
              ) : (
                <span />
              )}
              {canPinExchange ? (
                <Tooltip title="Pin this Q&A on the canvas">
                  <IconButton
                    size="small"
                    onClick={() => onPinExchange?.(index)}
                    aria-label="Pin question and answer on canvas"
                    className="board-chat-pin-exchange-btn"
                  >
                    <PushPinOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            ) : null}
            <Typography variant="body2" className="board-chat-message-content" component="div">
              {message.role === 'assistant' ? (
                <ChatAnswerParagraphs content={message.content} />
              ) : (
                message.content
              )}
            </Typography>
            {message.contextItems?.length ? (
              <Box className="board-chat-message-pins">
                {message.contextItems.map(item => {
                  const kind = item.kind || item.type;
                  const fallbackLabel = kind === 'user-pin'
                    ? formatContextChipLabel('user-pin', pinLabelById.get(item.id)?.replace(/^Pin · /, '') || item.title || item.id)
                    : kind === 'ai-pin'
                      ? formatContextChipLabel('ai-pin', item.title || item.label)
                      : formatContextChipLabel('shape', item.title || item.label || item.id);
                  const chipLabel = item.label || fallbackLabel;
                  const chipTitle = item.title || chipLabel;
                  return (
                    <Tooltip key={`${kind}-${item.id}`} title={chipTitle} enterDelay={400}>
                      <Chip
                        size="small"
                        variant="outlined"
                        className={contextChipClassName(kind)}
                        icon={<ContextChipIcon kind={kind} />}
                        label={chipLabel}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            ) : message.contextPinIds?.length || message.contextShapeIds?.length ? (
              <Box className="board-chat-message-pins">
                {(message.contextPinIds || []).map(pinId => (
                  <Chip
                    key={`pin-${pinId}`}
                    size="small"
                    variant="outlined"
                    icon={<PushPinOutlinedIcon />}
                    label={pinLabelById.get(pinId) || `Pin ${pinId.slice(-6)}`}
                  />
                ))}
                {(message.contextShapeIds || []).map(shapeId => (
                  <Chip
                    key={`shape-${shapeId}`}
                    size="small"
                    variant="outlined"
                    icon={<CropSquareOutlinedIcon />}
                    label={`Shape ${shapeId.slice(-6)}`}
                  />
                ))}
              </Box>
            ) : null}
            {message.actionsSummary ? (
              <Typography variant="caption" color="primary" className="board-chat-actions-summary">
                {message.actionsSummary}
              </Typography>
            ) : null}
            {message.sources?.length ? (
              <Box className="board-chat-sources">
                {message.sources.map(source => (
                  <SourceCard
                    key={source.url}
                    source={source}
                    onPinSource={onPinSource}
                  />
                ))}
              </Box>
            ) : null}
            {message.role === 'assistant' && message.artifact ? (
              <BoardChatArtifact
                artifact={message.artifact}
                onPlaceOnCanvas={onPlaceArtifact}
              />
            ) : null}
            {message.role === 'assistant' && message.jobMetrics ? (
              <BoardAiJobDebug
                metrics={message.jobMetrics}
                className="board-chat-message-job-debug"
              />
            ) : null}
            {message.role === 'assistant' && (message.aiInput || message.jobMetrics?.aiInput) ? (
              <BoardAiInputDebug
                input={message.aiInput || message.jobMetrics.aiInput}
                className="board-chat-message-input-debug"
              />
            ) : null}
          </Box>
          );
        })}
        {loading ? (
          <BoardAiThinking
            active
            variant="chat"
            className="board-chat-thinking"
            thinkingPhase={thinkingPhase}
          />
        ) : null}
        {!(Array.isArray(messages) && messages.some(m => m.role === 'assistant' && m.jobMetrics)) ? (
          <BoardAiJobDebug metrics={lastJobMetrics} className="board-chat-job-debug" />
        ) : null}
        {!(Array.isArray(messages) && messages.some(m => m.role === 'assistant' && (m.aiInput || m.jobMetrics?.aiInput))) ? (
          <BoardAiInputDebug input={lastJobMetrics?.aiInput} className="board-chat-input-debug" />
        ) : null}
        <div ref={messagesEndRef} />
      </Box>

      {error ? (
        <Typography variant="caption" color="error" className="board-chat-error">
          {error}
        </Typography>
      ) : null}

      <Box className="board-chat-composer">
        {composerContextItems.length > 0 ? (
          <Box className="board-chat-composer-context">
            {composerContextItems.map(item => (
              <Tooltip
                key={item.key}
                title={item.title || item.label}
                enterDelay={400}
              >
                <Chip
                  size="small"
                  className={contextChipClassName(item.kind)}
                  icon={<ContextChipIcon kind={item.kind} />}
                  label={item.label}
                  onDelete={() => onRemoveComposerContextItem(item.key)}
                />
              </Tooltip>
            ))}
          </Box>
        ) : null}
        <Box className="board-chat-input-row">
          <textarea
            ref={inputRef}
            rows={expanded ? 2 : 1}
            className="board-chat-input"
            placeholder={
              composerContextItems.length
                ? 'Ask anything...'
                : 'How can I help you?'
            }
            value={input}
            onChange={event => {
              onInputChange(event.target.value);
              resizeChatInput(event.target);
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <Box className="board-chat-composer-actions">
            <Tooltip title={activeChatModel?.detail || chatModelMenuLabel}>
              <span className="board-chat-model-wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={event => setModelMenuAnchor(event.currentTarget)}
                  disabled={loading || chatModelOptions.length === 0}
                  className="board-chat-model-btn"
                  aria-haspopup="listbox"
                  aria-expanded={Boolean(modelMenuAnchor)}
                  aria-label={
                    activeChatModel?.label
                      ? `${chatModelMenuLabel}: ${activeChatModel.label}`
                      : chatModelMenuLabel
                  }
                  endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                >
                  {activeChatModel?.label || 'Better'}
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={modelMenuAnchor}
              open={Boolean(modelMenuAnchor)}
              onClose={() => setModelMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              style={{ zIndex: 1500 }}
              MenuListProps={{ 'aria-label': chatModelMenuLabel }}
            >
              {chatModelOptions.map(option => (
                <MenuItem
                  key={option.id}
                  selected={option.id === chatModel}
                  disabled={Boolean(option.disabled)}
                  onClick={() => {
                    if (option.disabled) return;
                    onChatModelChange?.(option.id);
                    setModelMenuAnchor(null);
                  }}
                  className="board-chat-model-item"
                >
                  {option.id === chatModel ? (
                    <CheckIcon className="board-chat-model-check" sx={{ fontSize: 16 }} />
                  ) : (
                    <span className="board-chat-model-check" aria-hidden="true" />
                  )}
                  <Box className="board-chat-model-item-text">
                    <span className="board-chat-model-item-label">{option.label}</span>
                    {option.detail ? (
                      <span className="board-chat-model-item-detail">{option.detail}</span>
                    ) : null}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title={inAppAskLabel}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onSend}
                  disabled={loading || !input.trim()}
                  className="board-chat-send-btn"
                  aria-label={inAppAskLabel}
                >
                  <SendIcon sx={{ fontSize: 16 }} />
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      </Box>
    </Box>
    </>
  );
};

export default BoardChatPanel;
